import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText, FolderOpen, Pin, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert, Badge, EmptyState, Input, PageHeader, QueryError, Spinner } from '@/components/ui';
import { formatBytes, openStoredFile } from '@/lib/attachments';
import { useAuth } from '@/lib/auth';
import { db, unwrap } from '@/lib/db';
import { errorMessage, formatDate } from '@/lib/utils';
import type { OrgResource } from '@/types/db';

export async function openResource(resource: OrgResource) {
  if (resource.external_url) {
    window.open(resource.external_url, '_blank', 'noopener,noreferrer');
    return;
  }
  await openStoredFile(resource.storage_path!);
}

export function ResourcesPage() {
  const { activeOrgId, activeMembership } = useAuth();
  const [query, setQuery] = useState('');
  const [openError, setOpenError] = useState<string | null>(null);
  const resources = useQuery({
    queryKey: ['resources', activeOrgId],
    enabled: Boolean(activeOrgId),
    queryFn: async () =>
      unwrap(await db.from('org_resources').select('*').eq('org_id', activeOrgId!).eq('status', 'published').order('pinned', { ascending: false }).order('title')) as OrgResource[],
  });

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, OrgResource[]>();
    for (const r of resources.data ?? []) {
      if (q && ![r.title, r.description ?? '', r.category ?? ''].some((s) => s.toLowerCase().includes(q))) continue;
      const key = r.category || 'General';
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [resources.data, query]);

  if (!activeOrgId) {
    return (
      <div>
        <PageHeader title="Resources" />
        <EmptyState icon={<FolderOpen className="size-10" />} title="Join an organization">
          Policies, forms, and reference documents are published by your agency.
        </EmptyState>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Resources" subtitle={`Policies, SOPs, forms, and references from ${activeMembership?.organization.name}`} />
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search resources" className="h-12 pl-10" aria-label="Search resources" />
      </div>
      {openError && <Alert tone="danger" className="mb-3">{openError}</Alert>}
      {resources.isPending ? (
        <Spinner />
      ) : resources.isError ? (
        <QueryError error={resources.error} />
      ) : grouped.length === 0 ? (
        <EmptyState icon={<FolderOpen className="size-10" />} title="No resources yet" />
      ) : (
        <div className="space-y-5">
          {grouped.map(([category, items]) => (
            <section key={category}>
              <h2 className="label-caps mb-2 text-stone-500 dark:text-stone-400">{category}</h2>
              <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
                  {items.map((r) => (
                    <li key={r.id}>
                      <button type="button" className="flex w-full items-start gap-3 px-1 py-3 text-left transition-colors hover:bg-stone-100 dark:hover:bg-ink-900" onClick={() => openResource(r).catch((e) => setOpenError(errorMessage(e)))}>
                        {r.external_url ? <ExternalLink className="mt-0.5 size-4 text-stone-400" /> : <FileText className="mt-0.5 size-4 text-stone-400" />}
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5 font-medium">
                            {r.pinned && <Pin className="size-4 text-stone-400 dark:text-stone-500" />}
                            {r.title}
                            <Badge tone="neutral">{r.kind}</Badge>
                          </span>
                          {r.description && <span className="block text-sm text-stone-500">{r.description}</span>}
                          <span className="mt-0.5 block font-mono text-xs text-stone-500">
                            Updated {formatDate(r.updated_at)} {r.file_size ? `· ${formatBytes(r.file_size)}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
